//
// DISCLAIMER
//
// Copyright 2024-2026 ArangoDB GmbH, Cologne, Germany
//

import React from "react";
import { Button, ButtonGroup, Checkbox, CheckboxProps, Form, Modal } from "semantic-ui-react";
import { Notification, NotificationSeverity } from "../../api/lib";
import { FormActionButtonCancel, FormActionButtonSave } from "../../ui/lib";
import { dateForDateTimeInputValue } from "../../util/dateAndTimeUtils/DateTime"; // Add this import
import { DateTimePicker } from "./DateTimePicker";
import { defaultExpiresAt } from "./NotificationFields";

export type NotificationData = {
  notificationText: string;
  severity: NotificationSeverity;
  notificationId: string;
  expiresAt?: Date;
};
export const EditNotificationModal = ({
  onClose,
  onCreateNotification,
  onDeleteNotification,
  notificationData,
}: {
  onClose: () => void;
  onCreateNotification: (values: NotificationData) => Promise<void>;
  onDeleteNotification: (notificationId: string) => Promise<void>;
  notificationData: { id: string; notification: Notification };
}) => {
  const [notificationId, setNotificationId] = React.useState(notificationData.id);
  const [notificationText, setNotificationText] = React.useState(notificationData.notification.notification);
  const [severity, onSeverityChange] = React.useState(notificationData.notification.severity);
  const [expiresAt, setExpiresAt] = React.useState<Date | undefined>(
    notificationData.notification.expires_at ? new Date(notificationData.notification.expires_at) : undefined
  );
  const [processing, setProcessing] = React.useState(false);
  const onSubmit = async () => {
    setProcessing(true);
    // Do something with notification
    await onCreateNotification({
      notificationText: notificationText || "",
      severity: severity || NotificationSeverity.NOTIFICATION_SEVERITY_INFO,
      notificationId,
      expiresAt,
    });
    setProcessing(false);
    onClose();
  };
  return (
    <Modal open={true} size="large">
      <Modal.Header>Create new notification</Modal.Header>
      <Modal.Content scrolling>
        <Form onSubmit={onSubmit}>
          <Form.Field>
            <Form.Input
              name="notificationId"
              value={notificationId}
              label="Notification ID"
              placeholder="Notification ID"
              required
              onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => {
                setNotificationId(target.value);
              }}
            />
          </Form.Field>
          <Form.Field>
            <Form.TextArea
              name="notification"
              value={notificationText}
              label="Notification"
              placeholder="Notification"
              required
              onChange={({ target }: React.ChangeEvent<HTMLTextAreaElement>) => {
                setNotificationText(target.value);
              }}
            />
          </Form.Field>
          <Form.Field>
            <label>Expires at</label>
            <p>
              <small>After this time the notification is no longer shown and is removed by the inspector.</small>
            </p>
            <Checkbox
              toggle
              label="Never expires"
              checked={!expiresAt}
              onChange={(event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => {
                setExpiresAt(data.checked ? undefined : defaultExpiresAt());
              }}
            />
            {expiresAt && (
              // Rendered conditionally so the picker re-mounts with the default value after "Never expires" is switched off.
              <DateTimePicker
                dateTime={dateForDateTimeInputValue(new Date(expiresAt))}
                onClear={() => {
                  setExpiresAt(undefined);
                }}
                onChange={(newDateTime) => {
                  if (newDateTime) {
                    const localDate = new Date(newDateTime);
                    setExpiresAt(localDate);
                  } else {
                    setExpiresAt(undefined);
                  }
                }}
              />
            )}
          </Form.Field>
          <Form.Field>
            <label>Severity</label>
            <ButtonGroup>
              <Button
                type="button"
                content="Info"
                primary={severity === NotificationSeverity.NOTIFICATION_SEVERITY_INFO}
                onClick={() => onSeverityChange(NotificationSeverity.NOTIFICATION_SEVERITY_INFO)}
              />
              <Button
                type="button"
                content="Warning"
                primary={severity === NotificationSeverity.NOTIFICATION_SEVERITY_WARNING}
                onClick={() => onSeverityChange(NotificationSeverity.NOTIFICATION_SEVERITY_WARNING)}
              />
              <Button
                type="button"
                content="Critical"
                primary={severity === NotificationSeverity.NOTIFICATION_SEVERITY_CRITICAL}
                onClick={() => onSeverityChange(NotificationSeverity.NOTIFICATION_SEVERITY_CRITICAL)}
              />
            </ButtonGroup>
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button
          icon="trash"
          color="red"
          content="Delete"
          onClick={async () => {
            await onDeleteNotification(notificationId);
            onClose();
          }}
        />
        <FormActionButtonCancel icon="cancel" onClick={onClose} />
        <FormActionButtonSave primary type="submit" onClick={onSubmit} disabled={processing} />
      </Modal.Actions>
    </Modal>
  );
};
